/**
 * Standorte — Verwaltung der Lager-/Betriebsstandorte.
 *
 * Features:
 * - CRUD für Standorte (Name, Adresse)
 * - Fiscal-Year-Mapping (Geschäftsjahr-Zuordnung)
 * - Synchronisation mit Odoo Fiscal Positions
 *
 * @page /standorte
 * @roles admin, manager
 * @i18n locations, common
 * @endpoints GET/POST/PUT/DELETE /api/standorte, GET/PUT /api/standorte/fiscal-mapping, POST /api/standorte/fiscal-sync
 */
import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Modal } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  return fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then((r) => r.json());
}

// ─── UI Components ─────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-dim focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30"
    />
  );
}

// ─── Form ──────────────────────────────────────────────────────────

function StandortForm({ initial, onSave, onCancel, t }) {
  const [f, setF] = useState(initial || { name: '', adresse: '', betriebsmodell: 'eigen', jv_partner_name: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave(f);
  };

  return (
    <form onSubmit={submit}>
      <Field label={t('common.name')}><Input value={f.name} onChange={(e) => set('name', e.target.value)} required /></Field>
      <Field label={t('locations.address')}><Input value={f.adresse || ''} onChange={(e) => set('adresse', e.target.value)} /></Field>
      <Field label={t('locations.operatingModel')}>
        <select value={f.betriebsmodell || 'eigen'} onChange={(e) => set('betriebsmodell', e.target.value)}
          className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30">
          <option value="eigen">{t('locations.modelOwn')}</option>
          <option value="joint_venture">{t('locations.modelJV')}</option>
        </select>
      </Field>
      {f.betriebsmodell === 'joint_venture' && (
        <Field label={t('locations.jvPartnerName')}><Input value={f.jv_partner_name || ''} onChange={(e) => set('jv_partner_name', e.target.value)} /></Field>
      )}
      <div className="flex justify-end gap-3 mt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-inset transition-colors">{t('common.cancel')}</button>
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active/80 transition-colors">{t('common.save')}</button>
      </div>
    </form>
  );
}

// ─── Fiscal Mapping Section ────────────────────────────────────────

function FiscalMappingSection({ standorte, t }) {
  const [positions, setPositions] = useState([]);
  const [mappings, setMappings] = useState({});
  const [allStandorte, setAllStandorte] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);

  const loadMappings = useCallback(() => {
    api('/api/standorte/fiscal-mapping').then((res) => {
      if (res.available_positions) setPositions(res.available_positions);
      if (res.standorte) setAllStandorte(res.standorte);
      if (res.mappings) {
        const map = {};
        res.mappings.forEach((m) => { map[m.fiscal_position_id] = m.standort_id; });
        setMappings(map);
      }
    });
  }, []);

  useEffect(() => { loadMappings(); }, [loadMappings]);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    const arr = positions
      .filter((p) => mappings[p.id])
      .map((p) => ({ fiscal_position_id: p.id, fiscal_position_name: p.name, standort_id: mappings[p.id] }));
    const res = await api('/api/standorte/fiscal-mapping', { method: 'PUT', body: { mappings: arr } });
    setSaving(false);
    if (res.ok) setStatus({ type: 'success', msg: `${res.count} ${t('locations.mappingsSaved')}` });
    else setStatus({ type: 'error', msg: res.error || t('common.saveError') });
  };

  const handleSync = async () => {
    setSyncing(true);
    setStatus(null);
    const res = await api('/api/standorte/fiscal-sync', { method: 'POST', body: {} });
    setSyncing(false);
    if (res.error) {
      setStatus({ type: 'error', msg: res.error });
    } else {
      const parts = [];
      if (res.updated?.length) parts.push(`${res.updated.length} ${t('locations.customersUpdated')}`);
      if (res.noFiscal?.length) parts.push(`${res.noFiscal.length} ${t('locations.noFiscalPosition')}`);
      if (res.noMapping?.length) parts.push(`${res.noMapping.length} ${t('locations.noMapping')}`);
      setStatus({ type: parts.length === 0 || res.updated?.length > 0 ? 'success' : 'warn', msg: parts.join(' · ') || t('locations.noChanges') });
    }
  };

  // Use standorte prop if allStandorte not yet loaded
  const standortOptions = allStandorte.length > 0 ? allStandorte : standorte;
  const unmappedCount = positions.filter((p) => !mappings[p.id]).length;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-content">{t('locations.fiscalMapping')}</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('locations.fiscalMappingDesc')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSync} disabled={syncing}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-surface-inset border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-active transition-colors flex items-center gap-1.5 disabled:opacity-50">
            {syncing && <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" /></svg>}
            {t('locations.syncCustomers')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-surface-active text-content hover:bg-surface-active/80 transition-colors disabled:opacity-50">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {status && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-xs font-medium border ${
          status.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
          status.type === 'warn' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
          'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {status.msg}
        </div>
      )}

      {positions.length === 0 ? (
        <div className="text-center text-[var(--text-tertiary)] py-8 text-sm">{t('locations.noFiscalPositions')}</div>
      ) : (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left text-[10px] font-medium text-content-tertiary uppercase tracking-wider px-4 py-2.5">{t('locations.fiscalPosition')}</th>
                <th className="text-left text-[10px] font-medium text-content-tertiary uppercase tracking-wider px-4 py-2.5">{t('locations.location')}</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => {
                const isMapped = !!mappings[p.id];
                return (
                  <tr key={p.id} className={`border-b border-line last:border-b-0 ${!isMapped ? 'bg-amber-500/5' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm text-content font-medium">{p.name}</div>
                      <div className="text-[10px] text-[var(--text-tertiary)] font-mono">ID: {p.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={mappings[p.id] || ''}
                        onChange={(e) => setMappings((prev) => ({ ...prev, [p.id]: e.target.value ? Number(e.target.value) : null }))}
                        className={`bg-surface-inset border rounded-lg px-3 py-1.5 text-xs text-content focus:outline-none focus:border-amber-500/40 w-full max-w-xs ${
                          !isMapped ? 'border-amber-500/40' : 'border-line-hover'
                        }`}>
                        <option value="">— {t('locations.notAssigned')} —</option>
                        {standortOptions.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {unmappedCount > 0 && (
            <div className="px-4 py-2 bg-amber-500/5 border-t border-amber-500/20 text-[10px] text-amber-400">
              {unmappedCount} {t('locations.positionsUnmapped')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════

export default function StandortePage() {
  const { t } = useLanguage();
  const [standorte, setStandorte] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => { api('/api/standorte').then(setStandorte); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (s) => {
    if (s.id) {
      const { id, created_at, aktiv, ...body } = s;
      await api(`/api/standorte/${id}`, { method: 'PUT', body });
    } else {
      await api('/api/standorte', { method: 'POST', body: s });
    }
    setModal(null);
    load();
  };

  const remove = async (id) => {
    await api(`/api/standorte/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div className="p-page">
      <PageHeader title={t('locations.title')} subtitle={t('locations.subtitle')} className="mb-6" actions={
        <>
          <span className="text-sm text-[var(--text-secondary)] font-mono">{standorte.length} {t('locations.locations')}</span>
          <button onClick={() => setModal('new')} className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active/80 transition-colors flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            {t('locations.newLocation')}
          </button>
        </>
      } />

      <div className="space-y-2">
        {standorte.map((s) => (
          <div key={s.id} className="bg-surface-card border border-line rounded-card p-4 flex items-center justify-between shadow-card hover:border-line-hover transition-colors">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-content">{s.name}</span>
                {s.betriebsmodell === 'joint_venture' && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">JV{s.jv_partner_name ? ` · ${s.jv_partner_name}` : ''}</span>
                )}
              </div>
              {s.adresse && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{s.adresse}</div>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setModal(s)} className="p-1.5 rounded hover:bg-surface-active text-[var(--text-tertiary)] hover:text-content transition-colors"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
              <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-red-500/20 text-[var(--text-tertiary)] hover:text-red-400 transition-colors"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
            </div>
          </div>
        ))}
        {standorte.length === 0 && (
          <div className="text-center text-[var(--text-tertiary)] py-8">{t('locations.noLocations')}</div>
        )}
      </div>

      <FiscalMappingSection standorte={standorte} t={t} />

      <Modal open={modal !== null} onClose={() => setModal(null)} title={modal && modal !== 'new' ? t('locations.editLocation') : t('locations.newLocation')}>
        <StandortForm initial={modal !== 'new' ? modal : null} onSave={save} onCancel={() => setModal(null)} t={t} />
      </Modal>
    </div>
  );
}
